import {
  Alert,
  AlertVariants,
  BlotterTable,
  Box,
  type ColumnDef,
  EMPTY_ARRAY,
  HStack,
  type ILPSettlementBatchResponse,
  Text,
  useBehaviorSubject,
  useBlotterTable,
  VStack,
} from '@talos/kyoko';
import { useMemo } from 'react';

interface ManualBatchItem {
  RowID: string;
  Direction: 'Outgoing' | 'Incoming';
  Asset: string;
  Quantity: string;
  EquivalentAmount: string | undefined;
  EquivalentCurrency: string;
}

export interface ManualBatchPreviewSummaryData {
  counterparty: string;
  marketAccount: string;
  totalTransactions: string;
}

interface ManualBatchPreviewSummaryProps {
  summaryData: ManualBatchPreviewSummaryData;
  counterpartyDisplayName: string;
  marketAccountDisplayName: string;
  homeCurrency: string;
  settleBatchInstructions: NonNullable<ILPSettlementBatchResponse['SettleBatchInstructions']>;
  isPreviewLoading: boolean;
  isPreviewError: boolean;
  itemsBlotterTestId?: string;
  errorTestId?: string;
}

export function ManualBatchPreviewSummary({
  summaryData,
  counterpartyDisplayName,
  marketAccountDisplayName,
  homeCurrency,
  settleBatchInstructions,
  isPreviewLoading,
  isPreviewError,
  itemsBlotterTestId = 'manual-batch-items-blotter',
  errorTestId = 'manual-batch-error',
}: ManualBatchPreviewSummaryProps) {
  const columns = useMemo<ColumnDef<ManualBatchItem>[]>(() => {
    return [
      { type: 'text', field: 'Direction', title: 'Direction', width: 120, initialSort: '+' },
      { type: 'asset', field: 'Asset', title: 'Asset', width: 100 },
      {
        type: 'size',
        field: 'Quantity',
        title: 'Quantity',
        width: 150,
        params: {
          currencyField: 'Asset',
          showInTermsOfContracts: true,
          highlightNegative: true,
        },
      },
      {
        type: 'size',
        field: 'EquivalentAmount',
        title: `Equivalent (${homeCurrency})`,
        width: 150,
        params: { currencyField: 'EquivalentCurrency', highlightNegative: true },
      },
    ];
  }, [homeCurrency]);

  const { observable: dataObservable } = useBehaviorSubject(() => {
    const result: ManualBatchItem[] = (settleBatchInstructions ?? EMPTY_ARRAY).map((instruction, index) => ({
      RowID: index.toString(),
      Direction: instruction.Direction === 'Outbound' ? 'Outgoing' : 'Incoming',
      Asset: instruction.Asset,
      Quantity: instruction.Amount ?? '0',
      EquivalentAmount: instruction.EquivalentAmount,
      EquivalentCurrency: instruction.EquivalentCurrency,
    }));
    return {
      data: result,
      initial: true,
    };
  }, [settleBatchInstructions]);

  const blotterTable = useBlotterTable<ManualBatchItem>({
    dataObservable,
    errorObservable: undefined,
    columns,
    rowID: 'RowID' satisfies keyof ManualBatchItem,
    gridOptions: {
      loading: isPreviewLoading,
      domLayout: 'autoHeight',
      onFirstDataRendered: ({ api }) => {
        api.sizeColumnsToFit();
      },
    },
  });

  return (
    <>
      <VStack w="100%" gap="spacing8">
        <SettlementDetail title="Counterparty" text={counterpartyDisplayName} data-testid="counterparty" />
        <SettlementDetail title="Market Account" text={marketAccountDisplayName} data-testid="market-account" />
        <SettlementDetail
          title="Total Transactions"
          text={summaryData.totalTransactions}
          data-testid="total-transactions"
        />
      </VStack>

      {!isPreviewError && (
        <div data-testid={itemsBlotterTestId}>
          <BlotterTable {...blotterTable} />
        </div>
      )}
      {isPreviewError && (
        <Box mt="spacing12" w="100%" data-testid={errorTestId}>
          <Alert variant={AlertVariants.Negative} dismissable={false}>
            Failed to generate settlement preview
          </Alert>
        </Box>
      )}
    </>
  );
}

function SettlementDetail({
  title,
  text,
  ...props
}: {
  title: string;
  text: string | undefined;
  'data-testid'?: string;
}) {
  return (
    <HStack w="100%" justifyContent="space-between">
      <Text>{title}</Text>
      <Text color="colorTextAttention" {...props}>
        {text}
      </Text>
    </HStack>
  );
}
